import { FormInput, Button, Modal, Form, DatePickerUncontrolled, DateRangeSelect, ISelectType } from "@4uhub/lib4uhub";
import { Box, Divider, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import { IFilterParams } from "../models";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { filterModalSchema, TFilterModalSchema } from "./filtersModalSchema";
import React, { useCallback, useState } from "react";

const translationPath = "page.4uBus.logs.serviceReceived.";

const SELECT_PERIOD_OPTION = "select_period";

export const FiltersModal = ({
  open,
  filter,
  onClose,
  setFilter,
}: {
  open: boolean;
  filter: IFilterParams;
  onClose: () => void;
  setFilter: (params: Partial<IFilterParams>) => void;
}) => {
  const { t } = useTranslation();

  const methods = useForm<TFilterModalSchema>({
    resolver: zodResolver(filterModalSchema),
    defaultValues: {
      encounterNumber: filter.encounterNumber,
      encounterNumberOS: filter.encounterNumberOS,
      establishmentCodeOs: filter.establishmentCodeOs,
      individualDocumentOs: filter.individualDocumentOs,
      individualIdentifier: filter.individualIdentifier,
      individualIdentifierOs: filter.individualIdentifierOs,
      individualNameOs: filter.individualNameOs,
      encounterDateEntryStart: filter.encounterDateEntryStart,
      encounterDateEntryEnd: filter.encounterDateEntryEnd,
      encounterDateEntryOption: filter.encounterDateEntryOption
    },
  });

  const [enableFields, setEnableFields] = useState(
    filter.encounterDateEntryOption?.value !== SELECT_PERIOD_OPTION,
  );

  const handleCleanFilters = () => {
    methods.reset({
      encounterNumber: null,
      encounterNumberOS: null,
      establishmentCodeOs: null,
      individualDocumentOs: null,
      individualIdentifier: null,
      individualIdentifierOs: null,
      individualNameOs: null,
      encounterDateEntryStart: null,
      encounterDateEntryEnd: null,
      encounterDateEntryOption: null,
    });

    setFilter({
      encounterDateEntryStart: null,
      encounterDateEntryEnd: null,
      encounterDateEntryOption: null,
    });

    setEnableFields(true);
  };

  const onSubmit = () => {
    setFilter(methods.getValues() as IFilterParams);
    onClose();
  };

  const handleStartEncounterDateEntry = useCallback(
    (date: Date | null) => {
      setFilter({ encounterDateEntryStart: date });
      methods.setValue("encounterDateEntryStart", date, {
        shouldValidate: true,
      });
    },
    [methods, setFilter],
  );

  const handleEndEncounterDateEntry = useCallback(
    (date: Date | null) => {
      setFilter({ encounterDateEntryEnd: date });
      methods.setValue("encounterDateEntryEnd", date, { shouldValidate: true });
    },
    [methods, setFilter],
  );

  const handleRangeStartEncounterDateEntry = useCallback(
    (date: Date | null) => {
      if (!date) return;

      handleStartEncounterDateEntry(date);
    },
    [handleStartEncounterDateEntry],
  );

  const handleRangeEndEncounterDateEntry = useCallback(
    (date: Date | null) => {
      if (!date) return;

      handleEndEncounterDateEntry(date);
    },
    [handleEndEncounterDateEntry],
  );

  const handleDateRangeOptionChange = useCallback(
    (option: ISelectType | null) => {
      setFilter({ encounterDateEntryOption: option });
      methods.setValue("encounterDateEntryOption", option, {
        shouldValidate: true,
      });
    },
    [methods, setFilter],
  );

  return (
    <Modal open={open} onClose={onClose} maxDialogWidth="lg">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <FilterAltOutlinedIcon color="primary" />

        <Typography variant="h6" fontWeight="600">
          {t(translationPath + "more_filters")}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3, p: 2 }}>
        <Form {...methods}>
          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 1.1,
                mb: 1.5,
                display: "block",
              }}
            >
              {t(translationPath + "service_data")}
            </Typography>

            <Grid container spacing={2}>

              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="encounterNumber"
                  size="small"
                  label={t(translationPath + "generated_service_number")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="establishmentCodeOs"
                  size="small"
                  label={t(translationPath + "establishment_code")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="encounterNumberOS"
                  size="small"
                  label={t(translationPath + "external_service_number")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <DateRangeSelect
                  setEndDate={handleRangeEndEncounterDateEntry}
                  setStartDate={handleRangeStartEncounterDateEntry}
                  setEnableFields={setEnableFields}
                  value={filter.encounterDateEntryOption ?? null}
                  onValueSelected={handleDateRangeOptionChange}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePickerUncontrolled
                  value={filter.encounterDateEntryStart}
                  onChange={handleStartEncounterDateEntry}
                  label={`${t(translationPath + "start_date")}`}
                  isClearable={false}
                  disable={enableFields}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <DatePickerUncontrolled
                  value={filter.encounterDateEntryEnd}
                  onChange={handleEndEncounterDateEntry}
                  label={`${t(translationPath + "end_date")}`}
                  isClearable={false}
                  disable={enableFields}
                />
              </Grid>

            </Grid>
          </Box>

          <Box>
            <Typography
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              sx={{
                textTransform: "uppercase",
                letterSpacing: 1.1,
                mb: 1.5,
                display: "block",
              }}
            >
              {t(translationPath + "patient_data")}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="individualIdentifierOs"
                  size="small"
                  label={t(translationPath + "external_person_code")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="individualIdentifier"
                  size="small"
                  label={t(translationPath + "linked_person_code")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="individualDocumentOs"
                  size="small"
                  label={t(translationPath + "external_cpf")}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <FormInput
                  name="individualNameOs"
                  size="small"
                  label={t(translationPath + "external_person_name")}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </Form>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
        <Button color="inherit" onClick={handleCleanFilters}>
          {t(translationPath + "clean_filters")}
        </Button>

        <Box sx={{ display: "flex", gap: 3 }}>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            {t(translationPath + "cancel")}
          </Button>

          <Button
            variant="contained"
            disableElevation
            onClick={methods.handleSubmit(onSubmit)}
          >
            {t(translationPath + "apply_filters")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
